/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * News manage delete news confirm
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { useCallback, useEffect } from 'react';
import type { FC } from 'react';

import { Button } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { useTableListApi } from '@/main/modules/table';
import { icons } from '@assets/index';
import { ImgStyled } from '@components/common/Block/Img';
import { makeSelectPageNewsTranslation, makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import Modal from '@modules/modal/components/Modal';
import { ModalTitleStyled, ModalTitleTextStyled } from '@modules/modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import { stringFormatParams } from '@utils/stringFormat';

import { useDeleteManageNewsItemMutation } from '../../store';

interface IProps {
    newsId: string;
    newsTitle: string;
    onCloseModal: () => void;
}

export const NewsTableDeleteModal: FC<IProps> = ({ newsId, newsTitle, onCloseModal }) => {
    const dispatch = useDispatch();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const {
        modals: { deleteModal: tDeleteModal },
    } = useSelector(makeSelectPageNewsTranslation());

    const [deleteNewsItem, { isError, isLoading, isSuccess }] = useDeleteManageNewsItemMutation();

    const { initialLoad } = useTableListApi();

    useEffect(() => {
        if (isSuccess) {
            dispatch(
                modalSetStatusAction({
                    status: EModalStatus.success,
                }),
            );
        }
    }, [dispatch, isSuccess]);

    useEffect(() => {
        if (isError) {
            dispatch(
                modalSetStatusAction({
                    status: EModalStatus.failure,
                }),
            );
        }
    }, [dispatch, isError]);

    const handleDeleteItem = useCallback(() => {
        deleteNewsItem({ id: Number(newsId) })
            .unwrap()
            .then(() => {
                initialLoad();
            });
    }, [deleteNewsItem, initialLoad, newsId]);

    return (
        <Modal dataName="news-item-delete-modal" style={{ padding: '24px' }} width="480px">
            <Modal.Title className="modal-popup-title__base">
                <ModalTitleStyled>
                    <ModalTitleTextStyled>{tDeleteModal.title}</ModalTitleTextStyled>
                    <ImgStyled height="16px" src={icons.cancelSmallIcon} width="16px" onClick={onCloseModal} />
                </ModalTitleStyled>
            </Modal.Title>
            <Loader showLoader={isLoading}>
                <Modal.Status status={EModalStatus.start}>
                    <Modal.Text>{stringFormatParams(tDeleteModal.start.title, { title: newsTitle })}</Modal.Text>
                    <Modal.Text>{tDeleteModal.start.question}</Modal.Text>
                    <Modal.Control className="modal-popup-control__base margin-between">
                        <Button size="small" typographyProps={{ variant: 'h5' }} onClick={handleDeleteItem}>
                            {tCommon.modal.remove}
                        </Button>
                        <Button color="info" size="small" variant="text" onClick={onCloseModal}>
                            {tCommon.modal.cancel}
                        </Button>
                    </Modal.Control>
                </Modal.Status>
                <Modal.Status status={EModalStatus.success}>
                    <Modal.Text>{tDeleteModal.success}</Modal.Text>
                </Modal.Status>
                <Modal.Status status={EModalStatus.failure}>
                    <Modal.Text>{tDeleteModal.error}</Modal.Text>
                </Modal.Status>
            </Loader>
        </Modal>
    );
};
